import type { ApiSchemas } from '@rental/contracts/client'
import { format, formatDateTime } from '@rental/i18n'
import { Button } from '@rental/ui'
import { CheckCircle2, FileText } from 'lucide-react'
import { useRef, useState } from 'react'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { useLocale } from '../i18n'
import { ActivateDialog } from './ActivateDialog'

type Terms = ApiSchemas['StaffTerms']
type Version = Terms['versions'][number]

/**
 * OPS-1: every version of the terms, newest first, the one in force marked;
 * any can be opened in the editor (as the start of a new one) or put in force.
 * Nothing is ever deleted: a version a contract was signed on stays findable.
 */
export function VersionList({
  terms,
  opened,
  onOpen,
  onActivated,
}: {
  terms: Terms
  /** The version the editor started from. */
  opened: string | null
  onOpen: (version: string) => void
  onActivated: (version: string) => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.terms
  const dialog = useRef<HTMLDialogElement>(null)
  const [asked, setAsked] = useState<Version | null>(null)

  return (
    <Section title={copy.history}>
      {terms.versions.length === 0 ? (
        <p className="text-text-muted">{copy.noVersions}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {terms.versions.map((version) => (
            <li
              key={version.version}
              className="flex flex-col gap-2 rounded-sm bg-surface-well p-3"
              aria-label={format(copy.versionLabel, { version: version.version })}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="figures font-medium">{version.version}</span>
                {version.current ? <StatusChip tone="success">{copy.current}</StatusChip> : null}
                {version.draft ? <StatusChip tone="warning">{copy.draft}</StatusChip> : null}
                {version.version === opened ? (
                  <StatusChip tone="info">{copy.inEditor}</StatusChip>
                ) : null}
              </div>
              <p>{version.title}</p>
              {/* Q-162: whether these words let the second driver on the contract drive. */}
              <p className="text-sm">
                {version.secondDriverAllowed ? copy.secondDriverYes : copy.secondDriverNo}
              </p>
              <p className="text-sm text-text-muted">
                {format(copy.created, {
                  date: formatDateTime(locale, version.createdAt),
                  by: version.createdBy ?? (version.inApp ? copy.unknownAuthor : copy.fromFiles),
                })}
                {version.inForceSince
                  ? ` · ${format(copy.inForceSince, { date: formatDateTime(locale, version.inForceSince) })}`
                  : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                {version.version === opened ? null : (
                  <Button
                    variant="outline"
                    onClick={() => onOpen(version.version)}
                    aria-label={`${copy.open}: ${version.version}`}
                  >
                    <FileText aria-hidden="true" className="size-4" />
                    {copy.open}
                  </Button>
                )}
                {version.current ? null : (
                  <Button
                    variant="staff"
                    onClick={() => {
                      setAsked(version)
                      dialog.current?.showModal()
                    }}
                    aria-label={`${copy.activate}: ${version.version}`}
                  >
                    <CheckCircle2 aria-hidden="true" className="size-4" />
                    {copy.activate}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <ActivateDialog
        dialogRef={dialog}
        version={asked?.version ?? null}
        draft={asked?.draft ?? false}
        onDone={(version) => {
          setAsked(null)
          onActivated(version)
        }}
      />
    </Section>
  )
}
